import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { playerVerificationService } from '../../services/playerVerificationService';
import { useAuth } from '../../contexts/AuthContext';

const AdminPlayerVerification = () => {
  const { hasRole } = useAuth();
  const isAdmin = hasRole('admin');
  const [activeTab, setActiveTab] = useState('unverified');
  const [unverifiedPlayers, setUnverifiedPlayers] = useState([]);
  const [verifiedPlayers, setVerifiedPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [verificationNotes, setVerificationNotes] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (isAdmin) {
      fetchPlayers();
    }
  }, [isAdmin, activeTab]);

  const fetchPlayers = async () => {
    setLoading(true);
    try {
      if (activeTab === 'unverified') {
        const response = await playerVerificationService?.getUnverifiedPlayers();
        setUnverifiedPlayers(response?.data || []);
      } else {
        const response = await playerVerificationService?.getVerifiedPlayers();
        setVerifiedPlayers(response?.data || []);
      }
    } catch (error) {
      console.error('Error fetching players:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyPlayer = async (player) => {
    if (!window.confirm(`Are you sure you want to verify and activate ${player?.fullName}? This will make them visible in statistics and management panels.`)) return;
    
    try {
      await playerVerificationService?.verifyPlayer(player?.id, verificationNotes);
      setVerificationNotes('');
      await fetchPlayers();
      alert(`${player?.fullName} has been verified and activated successfully!`);
    } catch (error) {
      console.error('Error verifying player:', error);
      alert('Failed to verify player: ' + error?.message);
    }
  };

  const handleRevokeVerification = async (player) => {
    if (!window.confirm(`Are you sure you want to revoke verification for ${player?.fullName}? They will be hidden from statistics and management panels.`)) return;
    
    try {
      await playerVerificationService?.revokeVerification(player?.id);
      await fetchPlayers();
      alert(`Verification revoked for ${player?.fullName}`);
    } catch (error) {
      console.error('Error revoking verification:', error);
      alert('Failed to revoke verification: ' + error?.message);
    }
  };

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return 'N/A';
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today?.getFullYear() - birthDate?.getFullYear();
    const monthDiff = today?.getMonth() - birthDate?.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today?.getDate() < birthDate?.getDate())) {
      age--;
    }
    return age;
  };

  const formatPosition = (position) => {
    if (!position) return 'N/A';
    return position?.split('-')?.map(word => 
      word?.charAt(0)?.toUpperCase() + word?.slice(1)
    )?.join(' ');
  };

  const filteredPlayers = (activeTab === 'unverified' ? unverifiedPlayers : verifiedPlayers)?.filter(player => {
    const matchesSearch = player?.fullName?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         player?.currentClub?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         player?.nationalityPrimary?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    return matchesSearch;
  });

  if (!isAdmin) {
    return (
      <>
        <Helmet>
          <title>Access Denied - ProX11Market</title>
        </Helmet>
        <Header />
        <div className="min-h-screen bg-background pt-16 flex items-center justify-center">
          <div className="text-center">
            <Icon name="ShieldAlert" size={64} className="mx-auto mb-4 text-error" />
            <h1 className="text-2xl font-bold text-foreground mb-2">Access Denied</h1>
            <p className="text-muted-foreground">You do not have permission to access this page.</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Player Verification - Admin - ProX11Market</title>
      </Helmet>
      <Header />
      <div className="min-h-screen bg-background pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-12">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 mb-6 md:mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-2 font-headline">
                Player Verification Center
              </h1>
              <p className="text-sm md:text-base text-muted-foreground">
                Approve and activate player accounts before they appear in statistics and management panels
              </p>
            </div>
          </div>

          {/* Alert Banner */}
          {unverifiedPlayers?.length > 0 && (
            <div className="bg-warning/10 border border-warning/30 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <Icon name="AlertCircle" size={24} className="text-warning flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-semibold text-foreground mb-1">
                    {unverifiedPlayers?.length} Player{unverifiedPlayers?.length !== 1 ? 's' : ''} Awaiting Verification
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    These players have signed up but are not yet visible in statistics dropdowns or management panels.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Tabs */}
          <div className="flex gap-2 mb-6 border-b border-border">
            <button
              onClick={() => setActiveTab('unverified')}
              className={`px-4 py-2 font-medium transition-colors border-b-2 ${
                activeTab === 'unverified'
                  ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <div className="flex items-center gap-2">
                <Icon name="Clock" size={18} />
                Unverified ({unverifiedPlayers?.length})
              </div>
            </button>
            <button
              onClick={() => setActiveTab('verified')}
              className={`px-4 py-2 font-medium transition-colors border-b-2 ${
                activeTab === 'verified'
                  ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <div className="flex items-center gap-2">
                <Icon name="ShieldCheck" size={18} />
                Verified ({verifiedPlayers?.length})
              </div>
            </button>
          </div>

          {/* Search */}
          <div className="mb-6">
            <Input
              placeholder="Search by player name, club, or nationality..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              className="max-w-md"
            />
          </div>

          {/* Players Table */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : filteredPlayers?.length === 0 ? (
            <div className="bg-card border border-border rounded-lg p-12 text-center">
              <Icon name="Users" size={48} className="mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-semibold text-foreground mb-2">
                {searchTerm ? 'No Players Found' : `No ${activeTab === 'unverified' ? 'Unverified' : 'Verified'} Players`}
              </h3>
              <p className="text-muted-foreground">
                {searchTerm ? 'Try adjusting your search criteria' : `All players are currently ${activeTab === 'unverified' ? 'verified' : 'unverified'}`}
              </p>
            </div>
          ) : (
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted border-b border-border">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                        Player
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                        Position
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                        Age
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                        Current Club
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                        Signed Up
                      </th>
                      <th className="px-6 py-4 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {filteredPlayers?.map((player) => (
                      <tr key={player?.id} className="hover:bg-muted/50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center">
                              <Icon name="User" size={20} color="var(--color-muted-foreground)" />
                            </div>
                            <div>
                              <div className="font-medium text-foreground">{player?.fullName}</div>
                              <div className="text-sm text-muted-foreground">{player?.nationalityPrimary}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-foreground">{formatPosition(player?.primaryPosition)}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-foreground">{calculateAge(player?.dateOfBirth)}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-foreground">{player?.currentClub || 'Free Agent'}</div>
                          <div className="text-xs text-muted-foreground">{player?.currentLeague}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-muted-foreground">
                            {new Date(player?.createdAt)?.toLocaleDateString()}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          {activeTab === 'unverified' ? (
                            <Button
                              variant="default"
                              size="sm"
                              iconName="ShieldCheck"
                              iconPosition="left"
                              onClick={() => handleVerifyPlayer(player)}
                            >
                              Verify & Activate
                            </Button>
                          ) : (
                            <Button
                              variant="destructive"
                              size="sm"
                              iconName="ShieldOff"
                              iconPosition="left"
                              onClick={() => handleRevokeVerification(player)}
                            >
                              Revoke
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AdminPlayerVerification;